import moment from 'moment';
import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';
import { getDay } from 'date-fns';

//components
import withLocalization from '~/hoc/withLocalization';
import withRemoveDialog from '~/hoc/withRemoveDialog';
import Button from '~/components/CustomButton/CustomButton.jsx';
import TimePickerInput from '~/components/TimePicker';
import DatePicker from '~/components/DatePicker/DatePicker.jsx';

//elements
import Select from '~/elements/Select.js';
import LoadingSpinner from '~/elements/LoadingSpinner';
import { NumberInput } from '~/elements/Input.js';
import { Input } from "~/elements/Input";
import RequiredStar from '~/elements/RequiredStar.js';

//utils
import { validateRequestPayload } from '~/utils/commonMethods';
import { longDateFormat } from '../../../../../utils/dateFormat.js';
import { getMinutesFromTimeInput, minutesToTimeInput } from '../../../../../utils/timeCalc.js';
import { getWeekInCycleOptions, DEFAULT_VISIBILITY_WEEKS } from '~/utils/constants';

const getNextWeekday = (selectedDay) => {
    let today = moment();
    let nextDay = moment().day(selectedDay);
    if (nextDay.isSameOrBefore(today, 'day'))
        nextDay.add(7, 'days');
    return nextDay.format('YYYY-MM-DD');
}

class WorkHoursRulesForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            averageWorkHours: {
                name: null,
                department: null,
                maxHoursLimitInADay: 10,
                morningShiftsCount: 0,
                afternoonShiftsCount: 0,
                nightShiftsCount: 0,
                mandatoryOvertimeLimitPerWeek: 50,
                averageOvertimeLimitInPerWeek: 37.5,
                cycleWeekLength: 3,
                ruleCycleStartDate: getNextWeekday(1),
                maxConsecutiveWorkingSunday: 2,
                maxConsecutiveWorkingSaturday: 2,
                restBetweenShifts: 660,
                restBetweenWeek: 35,
                governmentApprovalForExceedingOvertime: false,
                workPatternVisibilityWeeks: null,
            },
            departments: [],
            standardWeekStartDay: 1,
        }
    }

    componentDidMount() {
        this.setState({ loading: true }, this.loadData);
    }

    loadData = async () => {
        const { currentEntity } = this.props.clientStore;
        const departments = currentEntity.client.data?.roles
        const transformedDepartments = departments.map(department => ({
            value: department.code,
            label: department.name
        }));

        transformedDepartments.unshift({
            value: 'z_none',
            label: 'Others',
        })

        this.setState({ departments: transformedDepartments })

        const { val } = this.props;

        if (val && Object.keys(val).length > 0) {
            this.setState({
                averageWorkHours: {
                    name: val.name || "",
                    department: val.department || "",
                    maxHoursLimitInADay: val.max_hours_limit_in_a_day / 60 || 10,
                    morningShiftsCount: val.morning_shifts_count || 0,
                    afternoonShiftsCount: val.afternoon_shifts_count || 0,
                    nightShiftsCount: val.night_shifts_count || 0,
                    mandatoryOvertimeLimitPerWeek: val.mandatory_overtime_limit_per_week / 60 || 50,
                    averageOvertimeLimitInPerWeek: val.average_overtime_limit_per_week / 60 || 30,
                    cycleWeekLength: val.cycle_week_length || 2,
                    ruleCycleStartDate: val.rule_cycle_start_date || getNextWeekday(1),
                    maxConsecutiveWorkingSunday: val.max_consecutive_working_sunday || 0,
                    maxConsecutiveWorkingSaturday: val.max_consecutive_working_saturday || 0,
                    restBetweenShifts: val.rest_between_shifts || 660,
                    restBetweenWeek: val.rest_between_week / 60 || 35,
                    workPatternVisibilityWeeks: val.work_pattern_visibility_weeks || DEFAULT_VISIBILITY_WEEKS,
                },
                loading: false
            });
        } else {
            // New entry - use default values
            this.setState({ loading: false });
        }
    }

    saveData = () => {
        const {
            name,
            department,
            maxHoursLimitInADay,
            morningShiftsCount,
            afternoonShiftsCount,
            nightShiftsCount,
            mandatoryOvertimeLimitPerWeek,
            averageOvertimeLimitInPerWeek,
            cycleWeekLength,
            ruleCycleStartDate,
            maxConsecutiveWorkingSunday,
            maxConsecutiveWorkingSaturday,
            restBetweenShifts,
            restBetweenWeek,
            workPatternVisibilityWeeks,
        } = this.state.averageWorkHours;
        const { updateData, t } = this.props;
        const workHoursRule = {
            id: this.props.val?.id || null,
            name: name,
            department: department,
            max_hours_limit_in_a_day: maxHoursLimitInADay * 60,
            morning_shifts_count: morningShiftsCount,
            afternoon_shifts_count: afternoonShiftsCount,
            night_shifts_count: nightShiftsCount,
            mandatory_overtime_limit_per_week: mandatoryOvertimeLimitPerWeek * 60,
            average_overtime_limit_per_week: averageOvertimeLimitInPerWeek * 60,
            cycle_week_length: cycleWeekLength,
            rule_cycle_start_date: moment(ruleCycleStartDate).format("YYYY-MM-DD"),
            max_consecutive_working_sunday: maxConsecutiveWorkingSunday,
            max_consecutive_working_saturday: maxConsecutiveWorkingSaturday,
            rest_between_shifts: restBetweenShifts,
            rest_between_week: restBetweenWeek * 60,
            work_pattern_visibility_weeks: workPatternVisibilityWeeks,
        };

        const validFields = ["name", "department"];
        const validMessage = ["Name", "Department"];
        const errors = validateRequestPayload(workHoursRule, validFields, validMessage, t);

        if (errors)
            return updateData({ hasError: true, errors: errors });

        this.props.updateData(workHoursRule);
    }

    handleChangeAndValidate = (name, value) => {
        const __averageWorkHours = this.state.averageWorkHours;

        if (['maxHoursLimitInADay', 'mandatoryOvertimeLimitPerWeek', 'averageOvertimeLimitInPerWeek', 'restBetweenWeek'].includes(name)) {
            const numValue = parseFloat(value);
            __averageWorkHours[name] = numValue;
        } else {
            __averageWorkHours[name] = value;
        }
        if (name === 'cycleWeekLength')
            __averageWorkHours["ruleCycleStartDate"] = getNextWeekday(this.state.standardWeekStartDay);

        this.setState({ averageWorkHours: __averageWorkHours })
    }

    handleMaximumHoursOT = (value) => {
        const { t } = this.props;
        if (!this.state.averageWorkHours.governmentApprovalForExceedingOvertime) {
            const __averageWorkHours = this.state.averageWorkHours;
            confirmAlert({
                title: t('Are You Sure'),
                message: t('Have you received government approval to use the average hours calculator for overtime payment'),
                buttons: [
                    {
                        label: t('Yes'),
                        onClick: () => {
                            __averageWorkHours["mandatoryOvertimeLimitPerWeek"] = 50;
                            __averageWorkHours["governmentApprovalForExceedingOvertime"] = true;
                            this.setState({ averageWorkHours: __averageWorkHours })
                        },
                    },
                    {
                        label: t('No'),
                        onClick: () => {
                            __averageWorkHours["governmentApprovalForExceedingOvertime"] = false;
                            this.setState({ averageWorkHours: __averageWorkHours });
                        }
                    },
                ],
            });
        }
        else {
            const __averageWorkHours = this.state.averageWorkHours;
            __averageWorkHours["mandatoryOvertimeLimitPerWeek"] = value;
            this.setState({ averageWorkHours: __averageWorkHours })
        }
    }

    render() {
        const { t } = this.props;
        const { currentEntity } = this.props.clientStore;
        const { dateTimeRules } = currentEntity.client.data;

        const { loading, departments } = this.state;

        const {
            name, department, maxHoursLimitInADay, morningShiftsCount, afternoonShiftsCount, nightShiftsCount,
            mandatoryOvertimeLimitPerWeek, averageOvertimeLimitInPerWeek, cycleWeekLength,
            ruleCycleStartDate, maxConsecutiveWorkingSunday, maxConsecutiveWorkingSaturday, restBetweenShifts, restBetweenWeek, workPatternVisibilityWeeks
        } = this.state.averageWorkHours;


        if (loading) return <LoadingSpinner />

        return (
            <>
                <div className='mt-2 custom-customer-header fw-600 fs-6'>{t('General Settings')}</div>
                <Row className='mb-2'>
                    <Col xs={6}>
                        <label className="control-label">{t('Name')} <RequiredStar /></label>
                        <Input
                            placeholder={t('Enter name')}
                            value={name}
                            onChange={e => this.handleChangeAndValidate('name', e.target.value)}
                        />
                        <i className='fs12 d-block'>{t('This name helps identify the work hours configuration')}.</i>
                    </Col>
                    <Col xs={6}>
                        <label className="control-label">{t('Department')} <RequiredStar /></label>
                        <Select
                            options={departments}
                            value={departments.find(item => item.value === department)}
                            onChange={e => this.handleChangeAndValidate('department', e.value)}
                        />
                        <i className='fs12 d-block'>{t('The department defines which group of employees this rule applies to')}.</i>
                    </Col>
                </Row>

                <div className='mb-2 custom-customer-header fw-600 fs-6'>{t('Cycle Settings')}</div>
                <Row className="mb-2">
                    <Col xs={6}>
                        <label className="control-label">{t('Work hours cycle length')}<RequiredStar /></label>
                        <Select
                            options={getWeekInCycleOptions(t, 2)}
                            value={getWeekInCycleOptions(t, 2).find(item => item.value === cycleWeekLength)}
                            onChange={evt => this.handleChangeAndValidate('cycleWeekLength', evt.value)}
                            style={{ width: '100%' }}
                        />
                        <i className='fs12 d-block'>{t('Select the cycle length you would like to plan the schedule for the members. This determines the span (in weeks) over which work hours are calculated')}.</i>
                    </Col>
                    <Col xs={6}>
                        <label className="control-label">{t('Work hours cycle start date')}<RequiredStar /></label>
                        <div className="slider-parent">
                            <DatePicker
                                selected={ruleCycleStartDate && new Date(ruleCycleStartDate)}
                                onChange={evt => this.handleChangeAndValidate('ruleCycleStartDate', moment(evt).format('YYYY-MM-DD'))}
                                dateFormat={longDateFormat(dateTimeRules)}
                                filterDate={date => getDay(date) === currentEntity?.client?.data?.basicRules?.standardWeekStartDay}
                                minDate={new Date()}
                            />
                        </div>
                        <i className='fs12 d-block'>{t('Select the date you want your cycle to start. Weekly work hour calculations and roster planning will be aligned based on this start date')}.</i>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col xs={6}>
                        <label className="control-label">{t('Maximum Weekly Hours for Average Calculation')} <a href='https://lovdata.no/lov/2005-06-17-62/§10-5' target="_blank">{t('Refer')} § 10-5.</a><RequiredStar /></label>
                        <NumberInput
                            width={"auto"}
                            value={mandatoryOvertimeLimitPerWeek}
                            onChange={e => this.handleMaximumHoursOT(e.target.value)}
                        />
                        <i className='fs12 d-block'>{t('This field sets the legally mandated upper limit of work hours per week. Requires government approval if exceeded')}.</i>
                    </Col>
                    <Col xs={6} >
                        <label className="control-label">{t('Standard Weekly Work Hours')} ({t('Without Break')}) <a href='https://lovdata.no/lov/2005-06-17-62/§10-4' target="_blank">{t('Refer')} § 10-4.</a><RequiredStar /></label>
                        <NumberInput
                            width={"auto"}
                            value={averageOvertimeLimitInPerWeek}
                            onChange={e => this.handleChangeAndValidate('averageOvertimeLimitInPerWeek', parseFloat(e.target.value).toFixed(1))}
                        />
                        <i className='fs12 d-block'>{t('This field sets the legally mandated upper limit of work hours per week')}.</i>
                    </Col>
                </Row>
                <Row className='mb-2'>
                    <Col xs={6}>
                        <label className="control-label">{t('Daily Work hours limit')} <a href='https://lovdata.no/lov/2005-06-17-62/§10-5' target="_blank">{t('Refer')} § 10-5.</a><RequiredStar /></label>
                        <NumberInput
                            width={"auto"}
                            value={maxHoursLimitInADay}
                            onChange={e => this.handleChangeAndValidate('maxHoursLimitInADay', parseFloat(e.target.value).toFixed(1))}
                        />
                        <i className='fs12 d-block'>{t('This is the maximum number of work hours allowed per day for the department, as per labor laws or internal policy. Refer § 10-5 for regulations')}.</i>
                    </Col>
                    <Col xs={6}>
                        <label className="control-label">{t('Work Pattern Visibility Weeks')} ({t('Automatic Work Patterns Only')})</label>
                        <Select
                            isClearable
                            options={getWeekInCycleOptions(t, 2)}
                            value={getWeekInCycleOptions(t, 2).find(item => item.value === workPatternVisibilityWeeks) || null}
                            onChange={e => this.handleChangeAndValidate('workPatternVisibilityWeeks', e ? e.value : null)}
                        />
                        <i className='fs12 d-block'>{t('Select how many weeks ahead you would like to publish automatic work patterns')}.</i>
                    </Col>
                </Row>

                <div className='mb-2 custom-customer-header fw-600 fs-6'>{t('Weekly Settings')}</div>
                <Row className="mb-2">
                    <Col xs={6}>
                        <label className="control-label">{t('Maximum consecutive working sunday in a cycle')}<RequiredStar /></label>
                        <NumberInput
                            width={"auto"}
                            value={maxConsecutiveWorkingSunday}
                            onChange={e => this.handleChangeAndValidate('maxConsecutiveWorkingSunday', parseInt(e.target.value))}
                        />
                        <i className='fs12 d-block'>{t('Defines how many Sundays an employee is allowed to work consecutively. Helps ensure weekly rest compliance')}.</i>
                    </Col>
                    <Col xs={6}>
                        <label className="control-label">{t('Maximum consecutive working saturday in a cycle')}<RequiredStar /></label>
                        <NumberInput
                            width={"auto"}
                            value={maxConsecutiveWorkingSaturday}
                            onChange={e => this.handleChangeAndValidate('maxConsecutiveWorkingSaturday', parseInt(e.target.value))}
                        />
                        <i className='fs12 d-block'>{t('Defines how many Saturdays an employee is allowed to work consecutively. Helps ensure weekly rest compliance')}.</i>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col xs={6}>
                        <Row>
                            <Col>
                                <label className="control-label">{t('Morning shift')}<RequiredStar /></label>
                                <NumberInput
                                    width={"auto"}
                                    value={morningShiftsCount}
                                    onChange={e => this.handleChangeAndValidate('morningShiftsCount', parseInt(e.target.value))}
                                />
                            </Col>
                            <Col>
                                <label className="control-label">{t('Afternoon shift')} <RequiredStar /></label>
                                <NumberInput
                                    width={"auto"}
                                    value={afternoonShiftsCount}
                                    onChange={e => this.handleChangeAndValidate('afternoonShiftsCount', parseInt(e.target.value))}
                                />
                            </Col>
                            <Col>
                                <label className="control-label">{t('Night shift')} <RequiredStar /></label>
                                <NumberInput
                                    width={"auto"}
                                    value={nightShiftsCount}
                                    onChange={e => this.handleChangeAndValidate('nightShiftsCount', parseInt(e.target.value))}
                                />
                            </Col>
                        </Row>
                        <i className='fs12 d-block'>{t('Configure how many shifts of each type (morning, afternoon, night) can be scheduled within week. Helps determine resource availability')}.</i>
                    </Col>
                </Row>

                <div className='mb-2 custom-customer-header fw-600 fs-6'>{t('Rest Period Settings')}</div>
                <Row className='mb-2'>
                    <Col xs={6}>
                        <label className="control-label mb-2">{t('Rest Between shifts')} <a href='https://lovdata.no/lov/2005-06-17-62/§10-8' target="_blank">{t('Refer')} § 10-5.</a><RequiredStar /></label>
                        <TimePickerInput
                            style={{ width: 150 }}
                            value={minutesToTimeInput(restBetweenShifts)}
                            format={dateTimeRules.time_format === "hh:mm" ? "HH:mm" : dateTimeRules.time_format}
                            onChange={e => this.handleChangeAndValidate('restBetweenShifts', getMinutesFromTimeInput(e))}
                        />
                        <i className='fs12 d-block'>{t('Minimum rest period in hours between the end of one shift and the start of the next. Helps avoid fatigue and ensures legal compliance')}.</i>
                    </Col>
                    <Col xs={6}>
                        <label className="control-label">{t('Rest Between week')} <a href='https://lovdata.no/lov/2005-06-17-62/§10-8' target="_blank">{t('Refer')} § 10-5.</a><RequiredStar /></label>
                        <NumberInput
                            width={"auto"}
                            value={restBetweenWeek}
                            onChange={e => this.handleChangeAndValidate('restBetweenWeek', parseFloat(e.target.value).toFixed(1))}
                        />
                        <i className='fs12 d-block'>{t('Minimum number of rest hours between consecutive work weeks. Ensures employees get proper rest over the weekend or shift cycle end')}.</i>
                    </Col>
                </Row>

                <div className='mb-4'>&nbsp;</div>

                <div className="position-fixed" style={{ bottom: 10, backgroundColor: "white" }}>
                    <Button variant="secondary"
                        onClick={this.saveData}
                        className="btn-wd btn-fill me-3">
                        {t('Save')}
                    </Button>
                    <Button variant="secondary" onClick={this.props.cancel} className="btn-wd">
                        {t('Cancel')}
                    </Button>
                </div>
            </>
        );
    }
}
export default inject('clientStore', 'commonStore')(withLocalization(withRemoveDialog(observer(WorkHoursRulesForm))));
